import { io, type Socket } from 'socket.io-client'
import { config } from '@/config/env'
import { vclog } from '../hooks/vc-log'

/**
 * Streams this participant's own mic audio to the transcription AI server's
 * /inmeet namespace, as 16kHz mono PCM16 chunks (see
 * public/transcription/pcm16-worklet.js).
 *
 * Deliberately a second, independent tap on the same track `useLocalMedia`
 * publishes — not a change to that pipeline. Whatever `localStream` carries
 * (raw mic, or NoiseCancellation's output when that's on) works here
 * unmodified: a MediaStreamTrack is a MediaStreamTrack regardless of which
 * AudioContext produced it, and WebAudio doesn't care.
 *
 * Results are NOT read back on this socket. They arrive for every
 * participant (including the speaker) over the room's existing Phoenix
 * channel — see useLiveCaptions — because the transcription server
 * broadcasts a finalized segment into the room the same way the H.323/SIP
 * bridges already do. This connection only uploads.
 *
 * No room token: this one transcription server is shared across every
 * new-inMeet environment (dev and production both), and each mints room
 * tokens with its own signing key, so a token minted here could never verify
 * against whichever key the shared server trusts. roomId/peerId/displayName
 * are sent and trusted as-is, same as inmeet_client_vc's legacy connection to
 * this same server already does.
 *
 * `backendUrl` is sent too — not a credential, just this environment's own
 * API base (already public in this build) — so the shared server can look up
 * which backend/signaling pair a finalized segment belongs to (its own
 * INMEET_ENV_MAP, server-side) instead of a single static destination that
 * could only ever be right for one environment at a time.
 */
export class LiveTranscriptionCapture {
  private context: AudioContext | null = null
  private socket: Socket | null = null
  private workletNode: AudioWorkletNode | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private silenceNode: GainNode | null = null

  get active(): boolean {
    return this.socket !== null
  }

  async start(track: MediaStreamTrack, roomId: string, peerId: string, displayName?: string | null): Promise<void> {
    await this.stop()

    const serviceUrl = config.transcription.serviceUrl
    if (!serviceUrl) return // not configured for this deployment — no-op

    if (typeof AudioWorkletNode === 'undefined') {
      vclog('producer', 'live transcription unavailable: no AudioWorklet', {})
      return
    }

    const socket = io(`${serviceUrl}/inmeet`, {
      auth: {
        roomId,
        peerId,
        displayName: displayName || undefined,
        backendUrl: config.apiUrl || window.location.origin,
      },
      transports: ['websocket'],
    })
    this.socket = socket

    socket.on('connect', () => {
      vclog('producer', 'live transcription connected', { roomId })
      socket.emit('startSpeechRecognize', { isStart: true })
    })
    socket.on('connect_error', (err: Error) => {
      // Non-critical enhancement — a participant's call keeps working with no
      // captions/transcript rather than surfacing this as a meeting error.
      vclog('producer', 'live transcription connect_error', { error: err.message })
    })
    socket.on('transcription_error', (payload: unknown) => {
      vclog('producer', 'live transcription error', { payload })
    })

    try {
      const context = new AudioContext()
      await context.audioWorklet.addModule('/transcription/pcm16-worklet.js')

      const source = context.createMediaStreamSource(new MediaStream([track]))
      const worklet = new AudioWorkletNode(context, 'pcm16-downsample')
      worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
        if (socket.connected) socket.emit('send_audio_data', event.data)
      }

      // The worklet has nothing anyone should hear — connected through a
      // silent gain to `destination` only because some engines throttle an
      // AudioWorkletNode's process() calls when nothing in its graph reaches
      // the output, the same reasoning noise-cancellation.ts documents for
      // its own graph.
      const silence = context.createGain()
      silence.gain.value = 0

      source.connect(worklet)
      worklet.connect(silence)
      silence.connect(context.destination)

      this.context = context
      this.sourceNode = source
      this.workletNode = worklet
      this.silenceNode = silence
    } catch (err) {
      vclog('producer', 'live transcription capture FAILED to start', {
        error: (err as Error).message,
      })
      await this.stop()
    }
  }

  async stop(): Promise<void> {
    if (this.socket) {
      const socket = this.socket
      this.socket = null
      try {
        socket.emit('endSpeechRecognize', { isStop: true })
      } catch {
        // best-effort
      }
      socket.disconnect()
    }

    if (this.workletNode) {
      this.workletNode.port.onmessage = null
      this.workletNode.disconnect()
      this.workletNode = null
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }
    if (this.silenceNode) {
      this.silenceNode.disconnect()
      this.silenceNode = null
    }
    if (this.context) {
      const context = this.context
      this.context = null
      try {
        await context.close()
      } catch {
        // already closed, or closing
      }
    }
  }
}
